import { toastController, type ToastOptions } from "@ionic/vue";

type QueuedToast = {
  options: ToastOptions;
  ready: Promise<void>;
};

const toastQueue: QueuedToast[] = [];

export function useToasts() {
  function getPositionAnchor() {
    const page =
      document.querySelector<HTMLElement>(
        "ion-modal .ion-page:not(.ion-page-hidden)",
      ) ??
      document.querySelector<HTMLElement>(
        "ion-app .ion-page:not(.ion-page-hidden)",
      );

    if (!page) {
      return undefined;
    }

    return (
      page.querySelector<HTMLElement>(".mini-player") ??
      page.querySelector<HTMLIonFooterElement>("ion-footer") ??
      page.querySelector<HTMLIonTabBarElement>("ion-tab-bar") ??
      undefined
    );
  }

  function isDuplicate(toast: ToastOptions) {
    return toastQueue.some(
      (queuedToast) => queuedToast.options.message === toast.message,
    );
  }

  function enqueue(toast: ToastOptions) {
    if (isDuplicate(toast)) {
      return;
    }

    toastQueue.push({
      options: toast,
      ready: new Promise<void>((resolve) => {
        setTimeout(resolve, 280);
      }),
    });

    if (toastQueue.length === 1) {
      void process();
    }
  }

  function showInfo(
    message: string,
    options?: Omit<ToastOptions, "color" | "message">,
  ) {
    enqueue({ ...options, message, color: "primary" });
  }

  function showSuccess(
    message: string,
    options?: Omit<ToastOptions, "color" | "message">,
  ) {
    enqueue({ ...options, message, color: "success" });
  }

  function showError(
    message: string,
    options?: Omit<ToastOptions, "color" | "message">,
  ) {
    enqueue({ ...options, message, color: "danger" });
  }

  async function process() {
    const queuedToast = toastQueue[0];

    if (!queuedToast) {
      return;
    }

    await queuedToast.ready;

    const controller = await toastController.create({
      duration: 5000,
      position: "bottom",
      positionAnchor: getPositionAnchor(),
      ...queuedToast.options,
    });

    await controller.present();
    await controller.onDidDismiss();

    toastQueue.shift();

    if (toastQueue.length > 0) {
      await process();
    }
  }

  return {
    showInfo,
    showSuccess,
    showError,
  };
}
